/**
 * Master v1.1 · Sample Parity Review + Activation Gate.
 * Left: corporate reference sample. Right: live v1.1 test render.
 * 21-row checklist (PASS / APPROVED DEVIATION / FAIL) → Super Admin approval flips production template.
 */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, RotateCcw, ShieldCheck, XCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { AdminLetterQA } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  PASS: "bg-emerald-500/20 text-emerald-200 border-emerald-500/50",
  APPROVED_DEVIATION: "bg-amber-500/20 text-amber-200 border-amber-500/50",
  FAIL: "bg-rose-500/20 text-rose-200 border-rose-500/50",
  PENDING: "bg-white/5 text-white/50 border-white/15",
};
const STATUS_LABEL = { PASS: "PASS", APPROVED_DEVIATION: "APPROVED DEVIATION", FAIL: "FAIL", PENDING: "PENDING" };

function Chip({ status, testId }) {
  return (
    <span data-testid={testId} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.PENDING}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function ChecklistRow({ row, draft, onChange }) {
  const cur = draft.status;
  return (
    <div data-testid={`qa-row-${row.section_no}`} className="grid grid-cols-[28px_1fr_auto] gap-2 items-start py-2 border-b border-white/5">
      <div className="text-[#D4AF37] font-mono text-xs pt-0.5">{String(row.section_no).padStart(2, "0")}</div>
      <div className="min-w-0">
        <div className="text-[12px] text-white/90">{row.title}</div>
        {cur === "APPROVED_DEVIATION" && (
          <input
            data-testid={`qa-note-${row.section_no}`}
            value={draft.note || ""}
            onChange={(e) => onChange({ ...draft, note: e.target.value })}
            placeholder="Deviation note (required)"
            className="mt-1 w-full rounded bg-white/5 border border-amber-500/30 px-2 py-1 text-[11px] text-white placeholder:text-white/30"
          />
        )}
        {row.reviewed_by && <div className="text-[10px] text-white/35 mt-0.5">{row.reviewed_by} · {new Date(row.reviewed_at).toLocaleString("en-IN")}</div>}
      </div>
      <div className="flex gap-1">
        {["PASS", "APPROVED_DEVIATION", "FAIL"].map((s) => (
          <button
            key={s}
            data-testid={`qa-set-${row.section_no}-${s}`}
            onClick={() => onChange({ ...draft, status: s })}
            title={STATUS_LABEL[s]}
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition ${cur === s ? STATUS_STYLE[s] : "border-white/10 text-white/40 hover:border-white/30"}`}
          >
            {s === "PASS" ? "P" : s === "FAIL" ? "F" : "D"}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminLetterQAPage() {
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [checkId, setCheckId] = useState("");
  const [sampleUrl, setSampleUrl] = useState(null);
  const [renderUrl, setRenderUrl] = useState(null);
  const [samplePdfUrl, setSamplePdfUrl] = useState(null);
  const [renderPdfUrl, setRenderPdfUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState({ sample: 6, render: 6 });
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);

  const load = useCallback(async () => {
    const s = await AdminLetterQA.status();
    setStatus(s);
    setDrafts(Object.fromEntries(s.checklist.map((r) => [r.section_no, { status: r.status, note: r.note || "" }])));
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load QA status"));
    AdminLetterQA.candidates().then((c) => setCandidates(c.candidates || [])).catch(() => {});
    AdminLetterQA.sampleBlobUrl().then(setSamplePdfUrl).catch(() => {});
  }, [load]);

  useEffect(() => {
    AdminLetterQA.samplePage(page)
      .then(({ url, pages }) => { setSampleUrl(url); setPageCount((c) => ({ ...c, sample: pages })); })
      .catch(() => toast.error("Reference sample page unavailable"));
  }, [page]);

  const render = useCallback(async (id, pg) => {
    setRendering(true);
    try {
      const { url, pages } = await AdminLetterQA.testRenderPage(pg, id || undefined);
      setRenderUrl(url);
      setPageCount((c) => ({ ...c, render: pages }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test render failed");
    } finally {
      setRendering(false);
    }
  }, []);

  useEffect(() => { render(checkId, page); }, [checkId, page, render]);
  useEffect(() => {
    AdminLetterQA.testRenderBlobUrl(checkId || undefined).then(setRenderPdfUrl).catch(() => {});
  }, [checkId]);

  const save = async () => {
    setSaving(true);
    try {
      const items = Object.entries(drafts).filter(([, d]) => d.status !== "PENDING")
        .map(([n, d]) => ({ section_no: Number(n), status: d.status, note: d.note || null }));
      const s = await AdminLetterQA.saveChecklist(items);
      setStatus(s);
      toast.success("Checklist saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const gate = async (kind) => {
    const reason = window.prompt(kind === "approve" ? "Approval reason (min 8 chars) — this makes Master v1.1 the LIVE production template:" : "Rollback reason (min 8 chars):");
    if (!reason) return;
    setGateBusy(true);
    try {
      if (kind === "approve") await AdminLetterQA.approve(reason); else await AdminLetterQA.rollback(reason);
      toast.success(kind === "approve" ? "Master v1.1 is now LIVE" : "Rolled back to Master v1.0");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Gate action failed");
    } finally {
      setGateBusy(false);
    }
  };

  if (!status) return <div className="p-10 text-white/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading QA gate…</div>;
  const live = status.active_template === status.candidate_template;
  const failCount = Object.values(drafts).filter((d) => d.status === "FAIL").length;
  const pendingCount = Object.values(drafts).filter((d) => d.status === "PENDING").length;

  return (
    <div data-testid="letter-qa-page" className="min-h-screen bg-[#07142e] text-white p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Eligibility Letter Engine</div>
          <h1 className="font-display text-2xl mt-1">Master v1.1 · Sample Parity Review</h1>
          <div className="text-[12px] text-white/55 mt-1">Signature rule: {status.signature_rules.pages_1_4} · {status.signature_rules.pages_5_6}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="qa-active-template" className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${live ? "border-emerald-400/50 text-emerald-200 bg-emerald-500/10" : "border-[#D4AF37]/50 text-[#F0D788] bg-[#D4AF37]/10"}`}>
            LIVE: {status.active_template}
          </span>
          {status.is_super_admin && !live && (
            <button data-testid="qa-approve-btn" disabled={!status.can_approve || gateBusy} onClick={() => gate("approve")}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-[#07142e] px-4 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110">
              <ShieldCheck size={13} /> APPROVE MASTER v1.1 FOR PRODUCTION
            </button>
          )}
          {status.is_super_admin && live && (
            <button data-testid="qa-rollback-btn" disabled={gateBusy} onClick={() => gate("rollback")}
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/50 text-rose-200 px-4 py-1.5 text-[11px] font-bold hover:bg-rose-500/10">
              <RotateCcw size={13} /> Rollback to {status.baseline_template}
            </button>
          )}
          {!status.is_super_admin && <span data-testid="qa-not-super-admin" className="text-[11px] text-white/40">Super Admin required to activate</span>}
        </div>
      </div>

      {!status.can_approve && !live && (
        <div data-testid="qa-gate-blocked" className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-[12px] text-amber-100 flex items-center gap-2">
          <AlertTriangle size={14} /> Activation gate blocked — {status.fail_count} FAIL · {status.pending_count} PENDING (saved). All 21 sections must be PASS or APPROVED DEVIATION.
        </div>
      )}
      {status.approval && (
        <div data-testid="qa-approval-record" className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-[12px] text-emerald-100 flex items-center gap-2">
          <CheckCircle2 size={14} /> Approved by {status.approval.approved_by} on {new Date(status.approval.approved_at).toLocaleString("en-IN")} — “{status.approval.reason}”
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="qa-page-nav">
        <span className="text-[11px] uppercase tracking-wider text-white/50">Compare page</span>
        {Array.from({ length: Math.max(pageCount.sample, pageCount.render) }, (_, i) => i + 1).map((n) => (
          <button key={n} data-testid={`qa-page-${n}`} onClick={() => setPage(n)}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition ${page === n ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-white/40"}`}>
            {n}
          </button>
        ))}
        <span className="text-[10px] text-white/40 ml-1">Sample {pageCount.sample} pp · Test render {pageCount.render} pp</span>
        <span className="text-[10px] text-white/40 ml-auto">Signatures expected: pages 1–4 none · pages 5–6 applicant only</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr_380px] gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col">
          <div className="px-3 py-2 flex items-center border-b border-white/10">
            <span className="text-[11px] uppercase tracking-wider text-white/60">Official sample (reference PDF) · page {page}</span>
            {samplePdfUrl && <a data-testid="qa-sample-open-pdf" href={samplePdfUrl} target="_blank" rel="noreferrer" className="ml-auto text-[10px] text-[#F0D788] underline">Open full PDF</a>}
          </div>
          <div className="h-[70vh] overflow-auto bg-[#0b1a3a] p-2">
            {sampleUrl ? <img data-testid="qa-sample-frame" alt={`Reference sample page ${page}`} src={sampleUrl} className="w-full shadow-xl" /> : <div className="p-6 text-white/40 text-sm">Loading sample…</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col">
          <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
            <span className="text-[11px] uppercase tracking-wider text-white/60">Test letter · {status.candidate_template} · page {page}</span>
            <select data-testid="qa-candidate-select" value={checkId} onChange={(e) => setCheckId(e.target.value)}
              className="ml-auto rounded bg-white/5 border border-white/15 text-[11px] px-2 py-1 text-white max-w-[200px]">
              <option value="">Synthetic QA applicant</option>
              {candidates.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.id.slice(0, 8)}{c.has_payload ? "" : " (fallback)"}</option>)}
            </select>
            {renderPdfUrl && <a data-testid="qa-render-open-pdf" href={renderPdfUrl} target="_blank" rel="noreferrer" className="text-[10px] text-[#F0D788] underline">PDF</a>}
            <button data-testid="qa-rerender-btn" onClick={() => render(checkId, page)} className="text-white/60 hover:text-white" title="Re-render">
              {rendering ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          </div>
          <div className="h-[70vh] overflow-auto bg-[#0b1a3a] p-2">
            {renderUrl ? <img data-testid="qa-render-frame" alt={`Test render page ${page}`} src={renderUrl} className="w-full shadow-xl" /> : <div className="p-6 text-white/40 text-sm">Rendering…</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-white/[0.03] p-3 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-wider text-[#D4AF37]">21-section checklist</div>
            <div className="flex gap-1.5 text-[10px]">
              <Chip status="FAIL" testId="qa-fail-count" /><span className="text-white/60">{failCount}</span>
              <Chip status="PENDING" testId="qa-pending-count" /><span className="text-white/60">{pendingCount}</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[62vh] pr-1">
            {status.checklist.map((row) => (
              <ChecklistRow key={row.section_no} row={row} draft={drafts[row.section_no] || { status: "PENDING", note: "" }}
                onChange={(d) => setDrafts((s) => ({ ...s, [row.section_no]: d }))} />
            ))}
          </div>
          <div className="pt-3 flex gap-2">
            <button data-testid="qa-mark-all-pass" onClick={() => setDrafts((s) => Object.fromEntries(Object.entries(s).map(([k, d]) => [k, { ...d, status: "PASS" }])))}
              className="rounded-full border border-emerald-500/40 text-emerald-200 px-3 py-1.5 text-[11px] hover:bg-emerald-500/10">Mark all PASS</button>
            <button data-testid="qa-save-checklist" disabled={saving} onClick={save}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#0A1F44] px-4 py-1.5 text-[11px] font-bold disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save review
            </button>
          </div>
          {failCount > 0 && <div className="mt-2 text-[10px] text-rose-300 flex items-center gap-1"><XCircle size={11} /> FAIL rows block activation.</div>}
        </div>
      </div>
    </div>
  );
}
